import React from 'react';
import { AlertCircle, RotateCcw, Save, Loader2 } from 'lucide-react';

export interface StickyActionBarProps {
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  onReset: () => void;
  onSave: () => void;
  t: (key: string) => string;
  /** Whether to show the reset button. Default: true */
  showReset?: boolean;
  /** Custom save button label. Default: uses t('Save Changes') */
  saveLabel?: string;
  /** Custom reset button label. Default: uses t('Reset') */
  resetLabel?: string;
  /**
   * Names the save / reset buttons for tests. Optional and purely additive: the buttons live inside
   * this component, so a caller has no other way to select them by anything but their label, which is
   * translated and therefore locale-dependent.
   */
  saveTestId?: string;
  resetTestId?: string;
}

/**
 * A sticky action bar for pages with save/reset functionality.
 *
 * Usage:
 * - Place inside the page content container (e.g., within `max-w-7xl mx-auto`)
 * - Use `sticky bottom-0` positioning (not `fixed`) to avoid overlaying footer/sidebar
 * - Remove save/reset buttons from page headers when using this component
 *
 * @example
 * ```tsx
 * <StickyActionBar
 *   hasUnsavedChanges={hasUnsavedChanges}
 *   isSaving={isSaving}
 *   onReset={handleReset}
 *   onSave={handleSave}
 *   t={t}
 * />
 * ```
 */
const StickyActionBar: React.FC<StickyActionBarProps> = ({
  hasUnsavedChanges,
  isSaving,
  onReset,
  onSave,
  t,
  showReset = true,
  saveLabel,
  resetLabel,
  saveTestId,
  resetTestId,
}) => {
  return (
    /* NARROW VIEWPORTS: at 390px the old single `flex items-center justify-between` row jammed the
       status icon and two translated button labels into one line; both labels wrapped, the bar grew
       to ~86px and — being `sticky bottom-0` — covered the field underneath it. Below `sm` the bar
       now STACKS (status on its own line, buttons full-width side by side) and the labels are
       `whitespace-nowrap`, so nothing wraps and the bar stays one predictable height. From `sm` up
       the layout is byte-for-byte the old one. */
    <div className="sticky bottom-0 bg-card border border-border rounded-xl shadow-lg mt-6 px-4 py-3 sm:px-6 sm:py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Rendered only when there IS something to say: an always-present empty slot would leave a
            stray 12px gap above the buttons in the stacked (narrow) layout. */}
        {hasUnsavedChanges && (
          <div className="flex items-center text-amber-600 text-sm min-w-0">
            <AlertCircle className="w-4 h-4 me-1 shrink-0" />
            <span className="truncate">{t('Unsaved changes')}</span>
          </div>
        )}

        <div className="flex items-center gap-3 sm:ms-auto">
          {showReset && (
            <button
              onClick={onReset}
              data-testid={resetTestId}
              disabled={!hasUnsavedChanges || isSaving}
              className="inline-flex flex-1 sm:flex-none items-center justify-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-700 bg-card border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-4 h-4 shrink-0" />
              {resetLabel || t('Reset')}
            </button>
          )}

          <button
            onClick={onSave}
            data-testid={saveTestId}
            disabled={isSaving || !hasUnsavedChanges}
            className={`inline-flex flex-1 sm:flex-none items-center justify-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              hasUnsavedChanges
                ? 'text-white bg-blue-600 hover:bg-blue-700'
                : 'text-gray-500 bg-gray-100 cursor-default'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
            ) : (
              <Save className="w-4 h-4 shrink-0" />
            )}
            {isSaving ? t('Saving...') : (saveLabel || t('Save Changes'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StickyActionBar;
